import React from "react";
import { CheckCheck, Inbox } from "lucide-react";

import { useAppStore } from "@/store/appStore";
import { useInAppNotificationsStore } from "@/store/notificationsStore";
import type { NotificationItem, NotificationSeverity } from "@/services/api";
import { cn } from "@/utils";

interface NotificationsPanelProps {
  onClose: () => void;
}

const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  info: "bg-primary-500",
  success: "bg-green-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ onClose }) => {
  const { items, unreadCount, isLoading, error, markRead, markAllRead } = useInAppNotificationsStore();
  const setCurrentPage = useAppStore((s) => s.setCurrentPage);

  const handleClick = async (item: NotificationItem) => {
    if (!item.is_read) {
      await markRead(item.id);
    }
    if (item.action_url) {
      // Простая внутренняя навигация через appStore.setCurrentPage,
      // если action_url ссылается на известный нам page.
      const map: Record<string, string> = {
        "/organization/billing": "organization_billing",
        "/organization/dashboard": "organization_dashboard",
      };
      const page = map[item.action_url];
      if (page) {
        setCurrentPage(page);
      } else {
        window.location.assign(item.action_url);
      }
      onClose();
    }
  };

  return (
    <div
      className={cn(
        "absolute top-full right-0 mt-2 w-[380px] z-50",
        "bg-surface-elevated border border-line rounded-lg shadow-lg overflow-hidden",
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-line">
        <span className="text-sm font-medium text-ink-1">
          Уведомления
          {unreadCount > 0 && <span className="ml-2 text-xs text-ink-3">· {unreadCount} новых</span>}
        </span>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => void markAllRead()}
            className="flex items-center gap-1 text-xs text-ink-3 hover:text-ink-1"
            aria-label="Прочитать все"
            title="Прочитать все"
          >
            <CheckCheck size={14} />
            Прочитать все
          </button>
        )}
      </div>

      <div className="max-h-[480px] overflow-y-auto">
        {isLoading && items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-ink-3">Загрузка…</div>
        ) : error && items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-10 text-ink-3">
            <Inbox size={32} className="mb-2 opacity-50" />
            <span className="text-sm">Уведомлений пока нет</span>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => void handleClick(item)}
                  className={cn(
                    "w-full text-left px-4 py-3 flex gap-3",
                    "hover:bg-surface-hover transition-colors",
                    !item.is_read && "bg-surface-emphasis/30",
                  )}
                >
                  <span className={cn("mt-1.5 w-2 h-2 rounded-full shrink-0", SEVERITY_DOT[item.severity])} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={cn("text-sm truncate", item.is_read ? "text-ink-2" : "text-ink-1 font-medium")}>
                        {item.title}
                      </span>
                      <span className="text-fs-2xs text-ink-4 shrink-0">{formatDateTime(item.created_at)}</span>
                    </div>
                    {item.body && <p className="text-xs text-ink-3 mt-1 line-clamp-2">{item.body}</p>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-line bg-surface-canvas">
        <button
          type="button"
          onClick={() => {
            setCurrentPage("notifications_page");
            onClose();
          }}
          className="text-xs text-ink-1 underline decoration-line-strong underline-offset-[3px] hover:decoration-ink-1"
        >
          Все уведомления
        </button>
        <button
          type="button"
          onClick={() => {
            setCurrentPage("notifications_preferences");
            onClose();
          }}
          className="text-xs text-ink-3 hover:text-ink-1"
        >
          Настройки
        </button>
      </div>
    </div>
  );
};

export default NotificationsPanel;
